import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { QualificationRulesMatrix } from '../components/trips/QualificationRulesMatrix';
import { companyApi } from '../api/company.api';
import { Company } from '../types/user';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../utils/toast';

// ─── Legend card ──────────────────────────────────────────────────────────────

const LegendItem: React.FC<{ color: string; label: string; description: string }> = ({
  color,
  label,
  description,
}) => (
  <div className="flex items-start gap-2">
    <span className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${color}`}>
      {label}
    </span>
    <span className="text-sm text-gray-600">{description}</span>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export const QualificationRulesPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin || false;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const loadCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const response = await companyApi.listCompanies();
        const list = response.data ?? [];
        setCompanies(list);
        // Pre-select the first active company
        // Leave selectedCompanyId as '' (= global defaults) on initial load
        // so the super admin sees and edits global rules first.
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Impossible de charger les entreprises');
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, [isSuperAdmin]);

  // Guard — only super admins may access this page
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* ── Page header ───────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Règles de qualification</h1>
            <p className="text-sm text-gray-500 mt-1">
              Configurez la qualification automatique des trajets selon les types de lieux.
            </p>
          </div>
        </div>

        {/* ── Company selector ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700 shrink-0">
              Entreprise
            </label>
            {isLoadingCompanies ? (
              <div className="h-9 w-48 bg-gray-200 animate-pulse rounded-md" />
            ) : (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                {/* value="" → companyId=null → global defaults */}
                <option value="">🌐 Règles globales (toutes les entreprises)</option>
                {companies.length > 0 && (
                  <option disabled>── Entreprises ──</option>
                )}
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                    {company.status !== 'active' ? ` (${company.status})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* ── Legend ────────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Légende des résultats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <LegendItem
              color="bg-green-100 text-green-700"
              label="PRO"
              description="Le trajet est automatiquement qualifié comme professionnel."
            />
            <LegendItem
              color="bg-orange-100 text-orange-700"
              label="PERSO"
              description="Le trajet est automatiquement qualifié comme personnel."
            />
            <LegendItem
              color="bg-blue-100 text-blue-700"
              label="Chauffeur"
              description="Le chauffeur devra qualifier manuellement le trajet."
            />
          </div>
          <div className="mt-3 space-y-1 text-xs text-gray-400">
            <p>
              <span className="font-medium text-gray-500">–</span>
              {' '}Aucune règle configurée — le chauffeur sera invité à qualifier manuellement.
            </p>
            <p>
              <span className="font-medium text-gray-500">(G)</span>
              {' '}Règle héritée des règles globales. Sélectionnez{' '}
              <span className="font-medium text-gray-500">🌐 Règles globales</span>
              {' '}dans le menu pour la modifier.
            </p>
            <p>
              Les règles d'une entreprise ont priorité sur les règles globales pour la même paire de lieux.
            </p>
          </div>
        </div>

        {/* ── Matrix ────────────────────────────────────────────────────────── */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              Matrice de qualification
            </h2>
            <div className="text-xs text-gray-500">
              Lignes = lieu de départ &nbsp;·&nbsp; Colonnes = lieu d'arrivée
            </div>
          </div>
          <QualificationRulesMatrix
            companyId={selectedCompanyId || null}
            isSuperAdmin={isSuperAdmin}
          />
        </div>

      </div>
    </Layout>
  );
};
